import React, { ReactNode, useMemo } from 'react';

import { GridMode } from './useEditableGridLogic';
import Button from '../button.component';

type Disabled = {
  add: boolean,
  edit: boolean,
  editAll: boolean,
  cancel: boolean,
  save: boolean,
  saveAll: boolean,
}

type ClickHandlers = {
  onAddClick?: false | (() => void),
  onEditAllClick?: false | (() => void),
  onCancelClick: () => void,
  onSaveClick: () => void,
}

type EditableGridButtonsProps = {
  mode: GridMode,
  disabled?: boolean | (Partial<Disabled> & { defaultState?: boolean }),
  extraButtonOnLeft?: ReactNode | ReactNode[],
  addButtonText?: string,
  mouseDownInsteadOfClick?: boolean,
} & ClickHandlers;


const createDisabledConfig = (defaultState: boolean): Disabled => ({
  add: defaultState,
  edit: defaultState,
  editAll: defaultState,
  cancel: defaultState,
  save: defaultState,
  saveAll: defaultState,
});

const useDisabledConfig = (disabled?: boolean | Partial<Disabled & { defaultState: boolean }>) => useMemo(() => {
  if (typeof disabled === 'object') {
    return { ...createDisabledConfig(Boolean(disabled.defaultState)), ...disabled };
  }
  return createDisabledConfig(Boolean(disabled));
}, [disabled]);

const handlersKeys = ['onAddClick', 'onEditAllClick', 'onCancelClick', 'onSaveClick'] as const;
const mapButtonHandlers = (
  handlers: ClickHandlers,
  mouseDownInsteadOfClick?: boolean
) => handlersKeys.reduce((acc, name) => {
  const handler = handlers[name] || (() => null);
  acc[name] = {
    onMouseDown: mouseDownInsteadOfClick ? handler : () => null,
    onClick: mouseDownInsteadOfClick ? () => null : handler,
    isVisible: !!handlers[name],
  };
  return acc;
}, {} as Record<typeof handlersKeys[number], {
  onClick: () => any,
  onMouseDown: () => any,
  isVisible: boolean,
}>);

const EditableGridButtons = ({
  mode,
  disabled,
  extraButtonOnLeft = [],
  addButtonText = 'Add',
  mouseDownInsteadOfClick,
  ...handlers
}: EditableGridButtonsProps) => {
  const disabledConfig = useDisabledConfig(disabled);
  const buttonHandlers = mapButtonHandlers(handlers, mouseDownInsteadOfClick);
  return (
    <div className="row no-gutters page-content-container align-items-end">
      <div className="editable-grid-buttons-extra-left-buttons">
        {extraButtonOnLeft}
      </div>
      <div className="ml-auto">
        {
          mode === GridMode.View && buttonHandlers.onAddClick?.isVisible && (
            <Button
              {...buttonHandlers.onAddClick}
              disabled={disabledConfig.add}
              kind="secondary"
            >
              { addButtonText }
            </Button>
          )
        }
        {
          mode === GridMode.View && buttonHandlers.onEditAllClick?.isVisible && (
            <Button
              {...buttonHandlers.onEditAllClick}
              disabled={disabledConfig.editAll}
              className="ml-2"
            >
              Edit All
            </Button>
          )
        }
        {
          (mode === GridMode.Edit || mode === GridMode.Add || mode === GridMode.EditAll) && (
            <>
              {
                mode === GridMode.EditAll
                  ? (
                    <Button
                      {...buttonHandlers.onSaveClick}
                      disabled={disabledConfig.saveAll}
                      kind="primary"
                    >
                      Save All
                    </Button>
                  )
                  : (
                    <Button
                      {...buttonHandlers.onSaveClick}
                      disabled={disabledConfig.save}
                    >
                      Save
                    </Button>
                  )
              }
              <Button
                {...buttonHandlers.onCancelClick}
                disabled={disabledConfig.cancel}
                className="ml-2"
                kind="secondary"
              >
                Cancel
              </Button>
            </>
          )
        }
      </div>
    </div>
  );
};

export default EditableGridButtons;
